import React, { useState } from 'react';
import { Calculator, TrendingUp, DollarSign, BarChart } from 'lucide-react';
import ModelCard from './models/ModelCard';
import ModelList from './models/ModelList';
import DCFAnalysis from './models/DCFAnalysis';
import ComparableAnalysis from './models/ComparableAnalysis';
import LBOAnalysis from './models/LBOAnalysis';
import SensitivityAnalysis from './models/SensitivityAnalysis';
import SynergiesAnalysis from './models/SynergiesAnalysis';
import { useDeals } from '../../store/DealsContext';

export default function FinancialModeling() {
  const { deals } = useDeals();
  const [activeModel, setActiveModel] = useState<string | null>(null);
  const [selectedDeal, setSelectedDeal] = useState<string | null>(null);

  const handleCloseModel = () => {
    setActiveModel(null);
    setSelectedDeal(null);
  };

  const selectedDealValue = selectedDeal 
    ? deals.find(d => d.id === selectedDeal)?.value || 0
    : 0;

  return (
    <div className="h-full space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Financial Modeling</h2>
          <p className="mt-2 text-gray-600">Analyze and value potential deals</p>
        </div>
        
        <div className="flex items-center space-x-4">
          <select
            value={selectedDeal || ''}
            onChange={(e) => setSelectedDeal(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Select Deal</option>
            {deals.map((deal) => (
              <option key={deal.id} value={deal.id}>{deal.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <ModelCard
          title="DCF Analysis"
          description="Calculate the present value of future cash flows"
          icon={<Calculator className="w-6 h-6 text-blue-600" />}
          onClick={() => setActiveModel('dcf')}
          disabled={!selectedDeal}
        />

        <ModelCard
          title="Comparable Analysis"
          description="Compare valuation metrics with similar companies"
          icon={<BarChart className="w-6 h-6 text-purple-600" />}
          onClick={() => setActiveModel('comparable')}
          disabled={!selectedDeal}
        />

        <ModelCard
          title="LBO Analysis"
          description="Model leveraged buyout scenarios"
          icon={<DollarSign className="w-6 h-6 text-green-600" />}
          onClick={() => setActiveModel('lbo')}
          disabled={!selectedDeal}
        />

        <ModelCard
          title="Sensitivity Analysis"
          description="Analyze value sensitivity to key variables"
          icon={<TrendingUp className="w-6 h-6 text-orange-600" />}
          onClick={() => setActiveModel('sensitivity')}
          disabled={!selectedDeal}
        />

        <ModelCard
          title="Synergies Analysis"
          description="Calculate potential merger synergies"
          icon={<BarChart className="w-6 h-6 text-indigo-600" />}
          onClick={() => setActiveModel('synergies')}
          disabled={!selectedDeal}
        />
      </div>

      <ModelList />

      {activeModel === 'dcf' && selectedDeal && (
        <DCFAnalysis onClose={handleCloseModel} />
      )}

      {activeModel === 'comparable' && selectedDeal && (
        <ComparableAnalysis onClose={handleCloseModel} />
      )}

      {activeModel === 'lbo' && selectedDeal && (
        <LBOAnalysis onClose={handleCloseModel} />
      )}

      {activeModel === 'sensitivity' && selectedDeal && (
        <SensitivityAnalysis 
          onClose={handleCloseModel}
          baseValue={selectedDealValue}
        />
      )}

      {activeModel === 'synergies' && selectedDeal && (
        <SynergiesAnalysis
          onClose={handleCloseModel}
          dealValue={selectedDealValue}
        />
      )}
    </div>
  );
}